import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { useQueryClient } from '@tanstack/react-query';
import { FormActionButtons } from '../../../components/Form/FormActionButton';
import { SignatureModal } from '../../../components/Form/SignatureModel';
// import { AttachmentSection } from '../../../components/Form/AttachmentSection';
import { PageLayoutWrapper } from '../../../components/FormPageLayoutWrapper';
import type { LegalAdviceFormSixData } from '../../../types/LegalAdvices/LegalAdviceFormSix.type';
import { DatalistInput } from '../components/dataListInput';
import { useSettings } from '../../../context/SettingContext';
import type { GeoLevel } from '../../../types/database';
import type { User } from '../../../types/user';
import { fetchLegalAdviceById, saveLegalAdviceDraft, submitLegalAdviceForm } from '../../../services/LegalAdviceSixService';
import { FormDatePicker } from '../../../components/Form/FormDatePicker';

const INITIAL_LEGAL_ADVICE_FORM_DATA: LegalAdviceFormSixData = {
  id: '',
  documentNumber: '',
  status: 'DRAFT',
  rejectionReason: '',
  submitDate: '',
  attachments: [],

  // Specific Form Details
  office: '',
    letterNo: '',
    year: '',
    month: '',
    day: '',
    refCourt: '',
    refLetterDate: '',
    refLetterNo: '',
    caseTypeNo: '',
    complainant: '',
    plaintiff: '',
    accused: '',
    law: '',
    lawSection: '',
    advocateName: '',
    serialNo: '',
    ccRecipientName: '',
    ccRecipientSerial: '',
    ccTownship: '',

    ccJudge: '',
    ccJudgeCourt: '',
    ccTownshipJudge: '',

    officerSignName: '',
    signatureImage: '',
};

interface LegalFormProps {
  id?: string;
  initialData?: Partial<LegalAdviceFormSixData>;
}

const getCurrentLocalDate = () => {
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${today.getFullYear()}-${month}-${day}`;
};

export function LegalFormSix({ id: propId, initialData }: LegalFormProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();

  const id = propId || params.id;
  const sigCanvasRef = useRef<SignatureCanvas | null>(null);

  const [formData, setFormData] = useState<LegalAdviceFormSixData>({
    ...INITIAL_LEGAL_ADVICE_FORM_DATA,
    ...initialData,
  });
  const [isPreview, setIsPreview] = useState(false);
  const [loading, setLoading] = useState<boolean>(Boolean(id));
  const [error, setError] = useState<string | null>(null);
  const [isSigning, setIsSigning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isApproved, setIsApproved] = useState<boolean>(
    initialData?.status === 'APPROVED' || formData.status === 'APPROVED'
  );
   const [selectedGeoLevels, setSelectedGeoLevels] = useState<GeoLevel[]>([]);
  const { settings } = useSettings();
  const OFFICE_OPTIONS = settings?.offices.map((o) => o.name) || [];
  const TOWNSHIP_OPTIONS = settings?.townships.map((t) => t.name) || [];

  const currentUser = JSON.parse(localStorage.getItem('user') || 'null') as User | null
  useEffect(() => {
    if (formData.status === 'APPROVED') {
      setIsApproved(true);
    }
  }, [formData.status]);

  // Fetch form data by ID
  useEffect(() => {
    if (!id) return;

    let isSubscribed = true;
    setLoading(true);
    setError(null);

    fetchLegalAdviceById(id)
      .then((data: LegalAdviceFormSixData) => {
        if (isSubscribed) {
          setFormData((prev) => ({
            ...prev,
            ...data,
            id: data.id || prev.id || id || '',
          }));
          if (data.status === 'APPROVED') {
            setIsApproved(true);
          }
        }
      })
      .catch((err: any) => {
        if (isSubscribed) {
          setError(err?.message || 'အချက်အလက်များ ရယူ၍ မရပါ');
        }
      })
      .finally(() => {
        if (isSubscribed) {
          setLoading(false);
        }
      });

    return () => {
      isSubscribed = false;
    };
  }, [id]);

  // Form is editable ONLY if status is DRAFT/RETURNED AND it has NOT been approved yet
  const isEditable = (formData.status === 'DRAFT') && !isApproved;

  const isFormSigned = Boolean(
    formData.signatureImage?.trim() && 
    (formData.officerSignName?.trim() || formData.signName?.trim())
  );

  // Navigation Handler
  const handleBackNavigation = async () => {
    await queryClient.invalidateQueries({ queryKey: ['legalFormSix'] });
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/legal-form-six');
    }
  };

  // Form Field Event Handlers
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!isEditable) return;
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // const handleClearDate = (name: keyof LegalAdviceFormSixData) => {
  //   if (!isEditable) return;
  //   setFormData((prev) => ({ ...prev, [name]: '' }));
  // };

  const handleClearSignature = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isEditable) return;
    setFormData((prev) => ({ ...prev, signatureImage: '' }));
  };

  // const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   if (!isEditable || !e.target.files) return;
  //   const filesArray = Array.from(e.target.files);
  //   setFormData((prev) => ({
  //     ...prev,
  //     attachments: [...(prev.attachments || []), ...filesArray],
  //   }));
  // };

  // const handleRemoveFile = (index: number) => {
  //   if (!isEditable) return;
  //   setFormData((prev) => ({
  //     ...prev,
  //     attachments: (prev.attachments || []).filter((_, i) => i !== index),
  //   }));
  // };

  const handleSaveSignature = () => {
    if (!isEditable) return;
    if (sigCanvasRef.current) {
      if (sigCanvasRef.current.isEmpty()) {
        alert('လက်မှတ် အရင်ရေးထိုးပေးပါရန်။');
        return;
      }
      try {
        const canvas = sigCanvasRef.current.getTrimmedCanvas();
        const base64Data = canvas.toDataURL('image/png');
        setFormData((prev) => ({ ...prev, signatureImage: base64Data }));
        setIsSigning(false);
      } catch {
        const rawCanvas = sigCanvasRef.current.getCanvas();
        const base64Data = rawCanvas.toDataURL('image/png');
        setFormData((prev) => ({ ...prev, signatureImage: base64Data }));
        setIsSigning(false);
      }
    }
  };

  const handleSaveDraft = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!isEditable || isSubmitting) return;

    try {
      setIsSubmitting(true);
      const payload: Partial<LegalAdviceFormSixData> = { ...formData, status: 'DRAFT' };

      const response = await saveLegalAdviceDraft(formData.id || id || null, payload);

      if (response && response.id) {
        setFormData((prev) => ({ ...prev, id: response.id, status: 'DRAFT' }));
      }

      alert('မူကြမ်း သိမ်းဆည်းပြီးပါပြီ (Draft Saved Successfully)');
    } catch (err: any) {
      console.error('Failed to save draft:', err);
      alert(err?.message || 'မူကြမ်း သိမ်းဆည်းခြင်း မအောင်မြင်ပါ။ ပြန်လည် ကြိုးစားပါ။');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handler for Approve & Save (အတည်ပြုသိမ်းဆည်းမည်)
  const handleApproveAndSave = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    const nameOfSigner = (formData.officerSignName || formData.signName || '').trim();
    if (!formData.signatureImage?.trim() || !nameOfSigner) {
      alert('လက်မှတ် နှင့် အမည် ဖြည့်သွင်းပေးပါရန်။');
      return;
    }

    try {
      setIsSubmitting(true);
      let targetId: string | undefined = formData.id || id;
      const submissionDate =
        formData.submitDate?.trim() || getCurrentLocalDate();

      const payload: LegalAdviceFormSixData = {
        ...formData,
        id: targetId || '',
        signerName: nameOfSigner,
        signName: nameOfSigner,
        submitDate: submissionDate,
        status: 'APPROVED',
      };

      const response = await saveLegalAdviceDraft(targetId || null, payload);
      const newId = response?.id || targetId;

      setFormData((prev) => ({
        ...prev,
        id: newId,
        signerName: nameOfSigner,
        signName: nameOfSigner,
        submitDate: submissionDate,
        status: 'APPROVED',
      }));

      setIsApproved(true); // Locks form editing
      alert('အတည်ပြု သိမ်းဆည်းပြီးပါပြီ (Approved & Saved Successfully)');
    } catch (err: any) {
      console.error('Failed to approve and save:', err);
      alert(err?.message || 'အတည်ပြု သိမ်းဆည်းခြင်း မအောင်မြင်ပါ။ ပြန်လည် ကြိုးစားပါ။');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handler for Submit To Superior (အထက်သို့ တင်ပြမည်)
  const handleSubmitToSuperior = async (e: React.MouseEvent,
    targetLevels?: GeoLevel[] | null
  ) => {
    e.preventDefault();
    if (isSubmitting) return;

     // Use passed levels if available; otherwise use multi-select state
      const levelsToSubmit =
        targetLevels && targetLevels.length > 0 ? targetLevels : selectedGeoLevels;

      // Validate that at least one checkbox level is selected
      if (!levelsToSubmit || levelsToSubmit.length === 0) {
        alert('အထက်သို့ တင်ပြရန် အဆင့် (Level) အနည်းဆုံးတစ်ခု ရွေးချယ်ပေးပါရန်။');
        return;
      }


    try {
      setIsSubmitting(true);
      let targetId: string | undefined = formData.id || id;
      const submissionDate =
        formData.submitDate?.trim() || getCurrentLocalDate();

      if (!targetId) {
        const draftResponse = await saveLegalAdviceDraft(null, { ...formData, status: 'DRAFT' });
        targetId = draftResponse?.id;
        if (!targetId) {
          throw new Error('အချက်အလက် သိမ်းဆည်းခြင်း အိုင်ဒီ (ID) မရရှိပါ');
        }

        setFormData((prev) => ({
          ...prev,
          ...draftResponse,
          id: targetId!,
        }));
      }

      const nameOfSigner = (formData.officerSignName || formData.signName || '').trim();
      const hasSigned = Boolean(formData.signatureImage?.trim() && nameOfSigner);
      // const nextStatus = hasSigned ? 'APPROVED' : 'SUBMITTED';

      if(!hasSigned){
        throw new Error('လက်မှတ်ထိုးပြီးမှသာ တင်ပြနိုင်ပါသည်');
      }

      const payload: LegalAdviceFormSixData = {
        ...formData,
        id: targetId,
        signerName: nameOfSigner,
        signName: nameOfSigner,
        status: 'SUBMITTED',
        submitDate: submissionDate,
        targetGeoLevels: levelsToSubmit, // Array of selected levels
      };

      const response = await submitLegalAdviceForm(targetId, payload);

      setFormData((prev) => ({
        ...prev,
        id: targetId,
        submitDate: submissionDate,
        status: response?.status || 'SUBMITTED',
      }));

      setIsApproved(true); // Locks form editing after submission
      alert(
        response?.status ==='SUBMITTED'
          ?  'အထက်သို့ တင်ပြပြီးပါပြီ (Submitted Successfully)' : 'တင်ပြမှုမအောင်မြင်ပါ'
      );
      //navigate('/legal-form-six');
    } catch (err: any) {
      console.error('Failed to submit form:', err);
      alert(err?.message || 'တင်ပြခြင်း မအောင်မြင်ပါ။ ပြန်လည် ကြိုးစားပါ။');
    } finally {
      setIsSubmitting(false);
    }
  };


  const handleClearDate = (name: keyof LegalAdviceFormSixData) => {
      if (!isEditable) return;
      setFormData((prev) => ({ ...prev, [name]: '' }));
    };

  const renderDatePicker = (
      name: Extract<keyof LegalAdviceFormSixData, string>
    ) => (
      <FormDatePicker
        name={name}
        value={(formData?.[name] as string) || ''}
        isEditable={isEditable}
        onChange={handleChange}
        onClear={handleClearDate}
      />
    );

  const GEO_LEVEL_MAP: Record<string, string> = {
    NATIONAL: 'ရုံးချုပ်',
    STATE: 'ပြည်နယ်/တိုင်း',
    DISTRICT: 'ခရိုင်',
    TOWNSHIP: 'မြို့နယ်',
  };

  const renderStageProgression = () => {
    const status = formData.status || 'DRAFT';

    // Extract and format Geo Level text
    const rawGeo = formData.targetGeoLevels || formData.geoLevel;
    const formatGeo = (geo: any) => {
      if (typeof geo === 'string') return GEO_LEVEL_MAP[geo] || geo;
      if (Array.isArray(geo)) {
        return geo
          .map((g) => GEO_LEVEL_MAP[g] || g)
          .filter(Boolean)
          .join(' | ');
      }
      return '';
    };
    const geoText = formatGeo(rawGeo);

    const STATUS_CONFIG: Record<
    string,
    { label: string; bg: string; text: string; border: string; dot: string }
  > = {
    DRAFT: {
      label: 'မူကြမ်း',
      bg: 'bg-slate-100',
      text: 'text-slate-600',
      border: 'border-slate-200',
      dot: 'bg-slate-400',
    },
    APPROVED: {
      label: 'ကိုယ်တိုင်အတည်ပြုပြီး',
      bg: 'bg-emerald-50',
      text: 'text-emerald-700',
      border: 'border-emerald-200',
      dot: 'bg-emerald-600',
    },
    SUBMITTED: {
      label: 'တင်ပြပြီး',
      bg: 'bg-amber-50',
      text: 'text-amber-700',
      border: 'border-amber-200',
      dot: 'bg-amber-500',
    },
    UNDER_REVIEW: {
      label: 'ကြည့်ရှုထားသည်',
      bg: 'bg-blue-50',
      text: 'text-blue-700',
      border: 'border-blue-200',
      dot: 'bg-blue-500',
    },
    ATTACHED: {
      label: 'ပြင်ဆင်ချက်/ညွှန်ကြားချက်',
      bg: 'bg-blue-50',
      text: 'text-blue-700',
      border: 'border-blue-200',
      dot: 'bg-blue-500',
    },
  };

    const currentConfig = STATUS_CONFIG[status] || STATUS_CONFIG.DRAFT;

      if (loading) {
        return (
          <div className="flex items-center justify-center p-12 text-slate-500">
            <span className="animate-spin mr-2">⏳</span> အချက်အလက်များ ရယူနေပါသည်...
          </div>
        );
      }

    return (
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-2.5 px-3.5 bg-slate-50 border border-slate-200 rounded-lg shadow-xs">
        {/* Left: Section Title & Location Subtext */}
        <div className="flex flex-col">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            လုပ်ငန်းစဉ် အခြေအနေ
          </span>
          {geoText && (
            <span className="text-xs text-slate-600 font-medium mt-0.5 flex items-center gap-1">
              <span className="text-slate-400">📍</span>
              <span>{geoText}</span>
            </span>
          )}
        </div>

        {/* Right: Modern Pill Badge */}
        <div
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${currentConfig.bg} ${currentConfig.text} ${currentConfig.border} self-start sm:self-center`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${currentConfig.dot}`} />
          <span>{currentConfig.label}</span>
        </div>
      </div>
    );
  };

  if (error) {
    return (
      <div className="p-6 text-center text-rose-600 bg-rose-50 rounded-xl border border-rose-200 my-6 max-w-xl mx-auto">
        <p className="font-semibold">{error}</p>
        <button
          type="button"
          onClick={handleBackNavigation}
          className="mt-4 px-4 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
        >
          နောက်သို့ ပြန်သွားမည်
        </button>
      </div>
    );
  }

  return (
    <PageLayoutWrapper
      handleBackNavigation={handleBackNavigation}
      renderStageProgression={renderStageProgression}
      isEditable={isEditable}
      isPreview={isPreview}
      readOnlyMessage={
        formData.status === 'APPROVED'
          ? 'စာရွက်စာတမ်းကို ကိုယ်တိုင်အတည်ပြုပြီးဖြစ်သဖြင့် ပြင်ဆင်၍ မရပါ'
          : formData.status === 'SUBMITTED'
            ? 'စာရွက်စာတမ်းကို အထက်သို့ တင်ပြပြီးဖြစ်သဖြင့် ပြင်ဆင်၍ မရပါ'
            : undefined
      }
      setIsPreview={setIsPreview}
    >
      {/* A4 Sheet Container */}
      <div className="w-full max-w-[800px] bg-white shadow-lg border border-gray-300 p-12 text-black leading-relaxed relative text-sm md:text-base">
        <div className="absolute top-4 right-4 text-xs font-bold">ဥပရအမှုလိုက်ပုံစံ (၆)</div>

        {/* Header Section */}
        <div className="text-center space-y-2 mt-4">
          <h1 className="text-lg font-bold">ပြည်ထောင်စုသမ္မတမြန်မာနိုင်ငံတော်အစိုးရ</h1>
          <h2 className="text-md font-bold">ပြည်ထောင်စုရှေ့နေချုပ်ရုံး</h2>
          <div className="flex justify-center items-center gap-2">
            <DatalistInput
              name="office"
              value={formData.office || ""}
              onChange={handleChange}
              placeholder=""
              options={OFFICE_OPTIONS}
            />
            <span className="w-12 text-left font-bold">ရုံး</span>
          </div>
        </div>

        {/* Meta Section (Letter No & Date Lines) */}
        <div className="flex flex-col items-end space-y-2 mt-6">
          <div className="flex items-center gap-1 w-64 justify-end">
            <span className="whitespace-nowrap">စာအမှတ်၊</span>
            <input 
              type="text" 
              name="letterNo"
              value={formData.letterNo}
              onChange={handleChange}
              className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none text-center"
            />
          </div>

          <div className="flex items-center gap-1 w-72 justify-end text-sm">
            <span className="whitespace-nowrap">ရက်စွဲ၊ ၂၀</span>
            <input 
              type="text" 
              name="year"
              value={formData.year}
              onChange={handleChange}
              className="w-8 bg-transparent border-b border-dotted border-black focus:outline-none text-center"
              placeholder=""
            />
            <span>ခုနှစ်၊</span>
            <input 
              type="text" 
              name="month"
              value={formData.month}
              onChange={handleChange}
              className="w-16 bg-transparent border-b border-dotted border-black focus:outline-none text-center"
            />
            <span>လ၊</span>
            <input 
              type="text" 
              name="day"
              value={formData.day}
              onChange={handleChange}
              className="w-8 bg-transparent border-b border-dotted border-black focus:outline-none text-center"
            />
            <span>ရက်</span>
          </div>
        </div>

        {/* Main Subject & Reference Grid */}
        <div className="mt-8 space-y-4">
          <div className="flex items-start">
            <span className="font-bold w-32 whitespace-nowrap">အကြောင်းအရာ။</span>
            <p className="font-bold flex-1 text-justify">
              သေဒဏ်ထိုက်သည့်ပြစ်မှုတွင် ဆင်းရဲနွမ်းပါးသော တရားခံအတွက် အစိုးရစရိတ်ဖြင့် ရှေ့နေငှားရမ်းလိုက်ပါခွင့်ပြုခြင်း။
            </p>
          </div>

          <div className="flex items-start">
            <span className="font-bold w-32 whitespace-nowrap">ရည်ညွှန်းချက်။</span>
            <div className="flex-1 flex flex-wrap items-center text-justify leading-8">
              <input 
                type="text" 
                name="refCourt" 
                value={formData.refCourt} 
                onChange={handleChange} 
                className="w-48 text-center bg-transparent border-b border-dotted border-black focus:outline-none mx-1"
                placeholder=""
              />
              တရားရုံး၏
             <div className="flex-grow">{renderDatePicker('refLetterDate')}</div>
              ရက်စွဲပါစာအမှတ်၊
              <input 
                type="text" 
                name="refLetterNo" 
                value={formData.refLetterNo} 
                onChange={handleChange} 
                className="w-36 text-center bg-transparent border-b border-dotted border-black focus:outline-none mx-1"
                placeholder=""
              />
            </div>
          </div>
        </div>

        {/* Paragraph Content Box */}
        <div className="mt-8 text-justify leading-9 pl-4">
          <input 
            type="text" 
            name="caseTypeNo" 
            value={formData.caseTypeNo} 
            onChange={handleChange} 
            className="w-56 text-center bg-transparent border-b border-dotted border-black focus:outline-none mx-1 font-bold"
            placeholder=""
          />
          တရားရုံး၊ ပြစ်မှုဆိုင်ရာ မူလမှု / အယူခံမှု / ပြင်ဆင်မှုအမှတ်
          <input 
            type="text" 
            name="complainant" 
            value={formData.complainant} 
            onChange={handleChange} 
            className="w-40 text-center bg-transparent border-b border-dotted border-black focus:outline-none mx-1"
            placeholder=""
          />
          ၊ တရားလို / အယူခံတရားလို / လျှောက်ထားသူ
          <input 
            type="text" 
            name="plaintiff" 
            value={formData.plaintiff} 
            onChange={handleChange} 
            className="w-40 text-center bg-transparent border-b border-dotted border-black focus:outline-none mx-1"
            placeholder=""
          />
           နှင့် တရားခံ / အယူခံတရားခံ / လျှောက်ထားခံရသူ
          <input 
            type="text" 
            name="accused" 
            value={formData.accused} 
            onChange={handleChange} 
            className="w-40 text-center bg-transparent border-b border-dotted border-black focus:outline-none mx-1"
            placeholder=""
          />
          ၊
          <input 
            type="text" 
            name="law" 
            value={formData.law} 
            onChange={handleChange} 
            className="w-40 text-center bg-transparent border-b border-dotted border-black focus:outline-none mx-1"
            placeholder=""
          />ဥပဒေ၊ ပုဒ်မ
          <input 
            type="text" 
            name="lawSection" 
            value={formData.lawSection} 
            onChange={handleChange} 
            className="w-36 text-center bg-transparent border-b border-dotted border-black focus:outline-none mx-1"
            placeholder=""
          />
          အရ စွဲဆိုသည့်အမှု / ပြစ်ဒဏ်ချမှတ်သည့်အမှုတွင် တရားခံ
          <input 
            type="text" 
            name="accused" 
            value={formData.accused}
            disabled
            className="w-40 text-center bg-transparent border-b border-dotted border-black focus:outline-none mx-1 bg-gray-50 text-gray-700"
          />
          အတွက် တရားလွှတ်တော် / အထက်တန်းရှေ့နေ ဦး / ဒေါ်
          <input 
            type="text" 
            name="advocateName" 
            value={formData.advocateName} 
            onChange={handleChange} 
            className="w-44 text-center bg-transparent border-b border-dotted border-black focus:outline-none mx-1 font-bold"
            placeholder=""
          />
          (စဉ်-
          <input 
            type="text" 
            name="serialNo" 
            value={formData.serialNo} 
            onChange={handleChange} 
            className="w-16 text-center bg-transparent border-b border-dotted border-black focus:outline-none mx-1"
            placeholder=""
          />
          ) အား အစိုးရစရိတ်ဖြင့် ငှားရမ်းလိုက်ပါဆောင်ရွက်ခွင့်ပြုကြောင်း အကြောင်းကြားပါသည်။
        </div>

        {/* Main Signature Section */}
        <div className="mt-12 pr-6 flex flex-col items-end space-y-3">
          <div className="w-64 space-y-3">
            <div className="flex items-center">
              <span className="w-24 text-right text-sm mr-2 whitespace-nowrap">လက်မှတ်</span>
              <div 
                onClick={() => setIsSigning(true)}
                className="flex-1 bg-transparent border-b border-dotted border-black min-h-[32px] flex items-center justify-center cursor-pointer hover:bg-gray-50 transition relative group"
              >
                {formData.signatureImage ? (
                  <>
                    <img 
                      src={formData.signatureImage} 
                      alt="Signature" 
                      className="h-8 object-contain mix-blend-multiply" 
                    />
                    <span className="absolute inset-0 bg-black/40 text-white text-[10px] opacity-0 group-hover:opacity-100 flex items-center justify-center transition rounded">
                      ပြင်ဆင်ရန်
                    </span>
                  </>
                ) : (
                  <span className="text-gray-400 text-xs font-sans">✍️ နှိပ်၍လက်မှတ်ထိုးရန်</span>
                )}
                {formData.signatureImage && (
                  <button
                    type="button"
                    onClick={handleClearSignature}
                    title="လက်မှတ်ရှင်းလင်းမည်"
                    className="absolute -right-6 text-slate-400 hover:text-rose-500 text-xs px-1 font-bold z-10 print:hidden"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>

            <div className="flex items-center">
              <span className="w-24 text-right text-sm mr-2 whitespace-nowrap">အမည်</span>
              <input
                type="text"
                name="officerSignName"
                value={formData.officerSignName}
                onChange={handleChange}
                className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none text-center text-xs"
              />
            </div>

            <div className="text-center text-sm font-bold py-1">
              တာဝန်ခံဥပဒေအရာရှိ
            </div>
          </div>
        </div>

        {/* CC Section (မိတ္တူကို-) */}
        <div className="mt-10 border-t border-gray-200 pt-6 space-y-3 text-sm">
          <div className="flex items-center gap-1">
            <span className="font-bold">ဦး / ဒေါ်</span>
            <input 
              type="text" 
              name="ccRecipientName" 
              value={formData.ccRecipientName} 
              onChange={handleChange} 
              className="w-44 text-center bg-transparent border-b border-dotted border-black focus:outline-none"
              placeholder=""
            />
            <span>(စဉ်-</span>
            <input 
              type="text" 
              name="ccRecipientSerial" 
              value={formData.ccRecipientSerial} 
              onChange={handleChange} 
              className="w-16 text-center bg-transparent border-b border-dotted border-black focus:outline-none"
              placeholder=""
            />
            <span>)</span>
          </div>

          <div className="pl-14 flex items-center gap-2">
            <DatalistInput
              name="ccTownship"
              value={formData.ccTownship}
              onChange={handleChange}
              placeholder=""
              options={TOWNSHIP_OPTIONS}
              className="w-40 text-center bg-transparent border-b border-black focus:outline-none"
            />
            <span className="font-bold">မြို့။</span>
          </div>

          <div className="font-bold pt-2">မိတ္တူကို -</div>
          
          <div className="pl-6 space-y-2 text-justify leading-7">
            <div className="flex items-center gap-1 flex-wrap">
              <input 
                type="text" 
                name="ccJudge" 
                value={formData.ccJudge} 
                onChange={handleChange} 
                className="w-48 text-center bg-transparent border-b border-dotted border-black focus:outline-none text-xs"
                placeholder=""
              />
              <span>တရားသူကြီး၊</span>
              
              
            </div>

            <div className="flex items-center gap-1 flex-wrap">
              <input 
                type="text" 
                name="ccJudgeCourt" 
                value={formData.ccJudgeCourt} 
                onChange={handleChange} 
                className="w-48 text-center bg-transparent border-b border-dotted border-black focus:outline-none text-xs"
                placeholder=""
              />
              <span>တရားရုံး။</span>
              
              
            </div>
            
            <div className="flex items-center gap-1 flex-wrap">
              <DatalistInput
                name="ccTownshipJudge"
                value={formData.ccTownshipJudge}
                onChange={handleChange}
                placeholder=""
                options={TOWNSHIP_OPTIONS}
                className="w-36 text-center bg-transparent border-b border-black focus:outline-none text-xs"
              />
              <span>မြို့။</span>
            </div>
          </div>
        </div>

        <FormActionButtons
            isPreview={isPreview}
            isEditable={isEditable}
            isSubmitting={isSubmitting}
            isFormSigned={isFormSigned}
            isApproved={isApproved}
            targetGeoLevels={formData.targetGeoLevels}
            currentGeoLevel={currentUser?.scopeLevel}
            selectedGeoLevels={selectedGeoLevels}
            onGeoLevelChange={setSelectedGeoLevels}
            onCancel={handleBackNavigation}
            onSaveDraft={handleSaveDraft}
            onApproveAndSave={handleApproveAndSave}
            onSubmitToSuperior={handleSubmitToSuperior}
          />

        {/* Attachment Section */}
        {/* <AttachmentSection
          attachments={formData.attachments}
          isEditable={isEditable}
          isPreview={isPreview}
          onFileChange={handleFileChange}
          onRemoveFile={handleRemoveFile}
        /> */}

        {/* Signature Modal */}
        <SignatureModal
          isOpen={isSigning}
          sigCanvasRef={sigCanvasRef}
          onClose={() => setIsSigning(false)}
          onClear={() => sigCanvasRef.current?.clear()}
          onSave={handleSaveSignature}
        />

        {/* <div className="absolute bottom-4 left-4 text-xs text-gray-400 print:bottom-0 print:left-0">
          လျှို့ဝှက်
        </div> */}

      </div>
    </PageLayoutWrapper>
  );
}